import { useAuth0 } from '@auth0/auth0-react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { axiosInstance as axios } from '../../../api/axios-instance';
import { IntegrationObjectService } from '../../../api/integration-object-service';
import { integrationObjectQueryKeys } from '../../../queries/use-integration-objects';
import {
  connectIntegration,
  disconnectIntegration,
  getAvailableIntegrations,
  getIntegrations as getConnectedIntegrations,
  getIntegrationByKey,
  getOAuthUrl,
  updateIntegrationApiKey,
  updateIntegrationConfig,
  triggerIntegrationSyncRun,
  getIntegrationTasks,
  fetchIntegrationObjectTasks,
  updateIntegrationObjectMeta,
  getUnprocessedItemCount,
  processUnprocessedItems,
  getMissingOrganizationsCount,
  fillMissingOrganizations,
  getFieldStatistics,
  prepareIntegrationMeta,
  testIntegrationCredentials,
  getMLProcessedCount,
  sendMLItemUpdate,
  updateExternalObject,
  addExternalObject,
  deleteExternalObject,
  AddExternalObjectRequest,
  uploadIntegrationRecords,
  IntegrationTask,
  TasksResponse,
  PrepareMetaResponse,
  TestCredentialsResponse,
  MLProcessedCountResponse,
  MLItemUpdateRequest,
  MLItemUpdateResponse,
  UpdateExternalObjectRequest,
  UpdateExternalObjectResponse,
  UploadRecordsRequest,
  UploadRecordsResponse,
  processUnprocessedSampleItems,
  triggerSyncCount,
  fullInitialSync,
  remap,
  calculateFieldStats,
  FullInitialSyncResponse,
  RemapResponse,
  CalculateFieldStatsResponse,
  saveIntegrationSelectedScopes,
  setupOriginObject,
  getOriginObjectSetupStatus,
  type SetupOriginObjectResponse,
  type OriginObjectSetupStatus,
  getIntegrationWebhooks,
  createIntegrationWebhook,
  updateIntegrationWebhook,
  deleteIntegrationWebhook,
  getIntegrationWebhookLogs,
  IntegrationWebhookRecord,
} from '../api/integrations2.0';
import { BagelObjectConf, ExternalObjectConf, Integration } from '@bageltechnologies/bagel-common';

// Query keys
export const integrationKeys = {
  all: ['integrations'] as const,
  available: () => [...integrationKeys.all, 'available'] as const,
  connected: () => [...integrationKeys.all, 'connected'] as const,
  detail: (key: string) => [...integrationKeys.all, 'detail', key] as const,
  originObjectSetupStatus: (key: string, originObjectKey: string) =>
    [...integrationKeys.all, 'originObjectSetupStatus', key, originObjectKey] as const,
};

// Standardized OAuth completion
export const completeOAuth = async (
  authToken: string,
  key: string,
  code: string,
  subdomain?: string,
): Promise<any> => {
  const config = {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  };

  try {
    // First, get the integration details to check if subdomain is required
    const integrationResponse = await axios.get(
      `/integrations/v2/${key}`,
      // `/authorize/oauth2`,
      config,
    );

    const integration = integrationResponse.data;

    // Check if subdomain is required based on integration configuration
    if (integration?.available?.oauth?.requiresSubdomain && !subdomain) {
      throw new Error(`Subdomain is required for ${key} integration`);
    }

    const response = await axios.post(`/authorize/oauth2`, { key: key, code, subdomain }, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Hooks
export const useAvailableIntegrations = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: integrationKeys.available(),
    queryFn: () => getAvailableIntegrations(auth0),
    enabled: !!auth0,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

export const useConnectedIntegrations = () => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: integrationKeys.connected(),
    queryFn: () => getConnectedIntegrations(auth0),
    enabled: !!auth0,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

export const useIntegration = (key: string) => {
  const auth0 = useAuth0();

  return useQuery({
    queryKey: integrationKeys.detail(key),
    queryFn: () => getIntegrationByKey(key, auth0),
    enabled: !!auth0 && !!key,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

export const useIntegrationConfig = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      config,
    }: {
      key: string;
      config: Partial<Omit<Integration, 'accountId'>> & { accountId?: string };
    }) => updateIntegrationConfig(key, config as Integration, auth0),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

export const useIntegrationConnection = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  const connectMutation = useMutation({
    mutationFn: ({ key, code }: { key: string; code: string }) =>
      connectIntegration(key, code, auth0),
    onSuccess: (data, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (key: string) => disconnectIntegration(key, auth0),
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });

  const getOAuthUrlMutation = useMutation({
    mutationFn: (key: string) => getOAuthUrl(key, auth0),
  });

  return {
    connect: connectMutation.mutateAsync,
    disconnect: disconnectMutation.mutateAsync,
    getOAuthUrl: getOAuthUrlMutation.mutateAsync,
    isConnecting: connectMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
    isGettingOAuthUrl: getOAuthUrlMutation.isPending,
  };
};

export const useIntegrationApiKey = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, tokenObject }: { key: string; tokenObject: any }) =>
      updateIntegrationApiKey(key, auth0, tokenObject),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

/**
 * Hook to update integration object metadata by triggering a metadata sync.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useUpdateIntegrationObjectMeta = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, object }: { key: string; object: string }) =>
      updateIntegrationObjectMeta(key, object, auth0),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

/**
 * Hook to trigger a sync run for one or more objects of an integration.
 * @returns {object} { triggerSyncRun, isLoading, isError, error, data }
 */
export const useTriggerIntegrationSyncRun = () => {
  const auth0 = useAuth0();
  return useMutation({
    mutationFn: ({
      key,
      objects,
      dateRange,
      isSampleRun = false,
    }: {
      key: string;
      objects: string[];
      dateRange?: {
        start: string;
        end: string;
        dateField?: string;
      };
      isSampleRun?: boolean;
    }) => triggerIntegrationSyncRun(auth0, key, objects, dateRange, isSampleRun),
  });
};

/**
 * Hook to fetch a paginated list of tasks for an integration.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param skip How many tasks to skip (for pagination, default 0)
 * @param limit How many tasks to return (default 20)
 * @param status Optional status parameter for filtering tasks (can be comma-separated string)
 * @returns {object} { data: TasksResponse, isLoading, isError, error, refetch }
 */
export const useIntegrationTasks = (key: string, skip = 0, limit = 20, status?: string) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ['integrationTasks', key, skip, limit, status],
    queryFn: () => getIntegrationTasks(auth0, key, skip, limit, status),
    enabled: !!auth0 && !!key,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to fetch paginated tasks for a specific integration object.
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object name (e.g., 'accounts')
 * @param skip How many tasks to skip (for pagination, default 0)
 * @param limit How many tasks to return (default 20)
 * @param status Optional status parameter for filtering tasks (can be comma-separated string)
 * @returns {object} { data: TasksResponse, isLoading, isError, error, refetch }
 */
export const useIntegrationObjectTasks = (
  key: string,
  object: string,
  skip = 0,
  limit = 20,
  status?: string,
) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ['integrationObjectTasks', key, object, skip, limit, status],
    queryFn: () => fetchIntegrationObjectTasks(auth0, key, object, skip, limit, status),
    enabled: !!auth0 && !!key && !!object,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to get the count of processed and unprocessed items for a given integration and object.
 * @param key Integration key (e.g., 'airtable')
 * @param originObjectKey Object name (e.g., 'survey')
 * @returns {object} { data, isLoading, isError, error, refetch } where data = { processed, unprocessed }
 */
export const useUnprocessedItemCount = (key: string, originObjectKey: string) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ['unprocessedItemCount', key, originObjectKey],
    queryFn: () => getUnprocessedItemCount(auth0, key, originObjectKey),
    enabled: !!auth0 && !!key && !!originObjectKey,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to process all unprocessed items for a given integration and object.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useProcessUnprocessedItems = () => {
  const auth0 = useAuth0();
  return useMutation({
    mutationFn: ({ key, originObjectKey }: { key: string; originObjectKey: string }) =>
      processUnprocessedItems(auth0, key, originObjectKey),
  });
};

/**
 * Hook to process all unprocessed sample items for a given integration and object.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useProcessUnprocessedSampleItems = () => {
  const auth0 = useAuth0();
  return useMutation({
    mutationFn: ({ key, originObjectKey }: { key: string; originObjectKey: string }) =>
      processUnprocessedSampleItems(auth0, key, originObjectKey),
  });
};

/**
 * Hook to get the count of missing organizations for a given integration and object.
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object2.0 name (e.g., 'opportunity')
 * @param originObjectKey Origin object key from external system (e.g., 'Opportunity')
 * @returns {object} { data, isLoading, isError, error, refetch } where data contains missing orgs count info
 */
export const useMissingOrganizationsCount = (
  key: string,
  object: string,
  originObjectKey: string,
) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ['missingOrganizationsCount', key, object, originObjectKey],
    queryFn: () => getMissingOrganizationsCount(auth0, key, object, originObjectKey),
    enabled: !!auth0 && !!key && !!object && !!originObjectKey && object !== 'account',
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to fill missing organizations for a given integration and object.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useFillMissingOrganizations = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      object,
      originObjectKey,
    }: {
      key: string;
      object: string;
      originObjectKey: string;
    }) => fillMissingOrganizations(auth0, key, object, originObjectKey),
    onSuccess: (_, { key, object, originObjectKey }) => {
      // Invalidate the missing organizations count query to refresh the data
      queryClient.invalidateQueries({
        queryKey: ['missingOrganizationsCount', key, object, originObjectKey],
      });
      // Also invalidate related integration queries
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
    },
  });
};

/**
 * Field statistics (stats only), fetched when `enabled`. Merge `items` onto `fieldsMeta` by
 * `fieldKey`. Admin variant of the dashboard's `useFieldStatistics` (PR #2104) — `accountId`
 * comes from the /account/:accountId route.
 * @returns {object} { data: FieldStatisticsResponse, isLoading, isError, error, refetch }
 */
export const useFieldStatistics = (key: string, originObjectKey: string, enabled = false) => {
  const { accountId = '' } = useParams<{ accountId: string }>();
  return useQuery({
    queryKey: ['fieldStatistics', accountId, key, originObjectKey],
    queryFn: () => getFieldStatistics(accountId, key, originObjectKey),
    enabled: !!accountId && !!key && !!originObjectKey && enabled,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

/**
 * Hook to prepare integration metadata by syncing and fetching available objects.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const usePrepareIntegrationMeta = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => prepareIntegrationMeta(key, auth0),
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

/**
 * Hook to test integration credentials and save the test result.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useTestIntegrationCredentials = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => testIntegrationCredentials(key, auth0),
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

/**
 * Hook to get ML processed count for a specific integration object.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param originObjectKey Object name (e.g., 'Case', 'Ticket')
 * @param enabled Whether to fetch the data (default true)
 * @returns {object} { data: MLProcessedCountResponse, isLoading, isError, error, refetch }
 */
export const useMLProcessedCount = (key: string, originObjectKey: string, enabled = true) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ['mlProcessedCount', key, originObjectKey],
    queryFn: () => getMLProcessedCount(key, originObjectKey, auth0),
    enabled: !!auth0 && !!key && !!originObjectKey && enabled,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to send ML item update for a specific integration object.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useSendMLItemUpdate = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      originObjectKey,
      updateData,
    }: {
      key: string;
      originObjectKey: string;
      updateData: MLItemUpdateRequest;
    }) => sendMLItemUpdate(key, originObjectKey, updateData, auth0),
    onSuccess: (_, { key, originObjectKey }) => {
      // Invalidate the ML processed count query to refresh the data
      queryClient.invalidateQueries({
        queryKey: ['mlProcessedCount', key, originObjectKey],
      });
      // Also invalidate related integration queries
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
    },
  });
};

/**
 * Hook to update an external object configuration for a specific integration.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useAddExternalObject = () => {
  const queryClient = useQueryClient();
  const { accountId = '' } = useParams<{ accountId: string }>();

  return useMutation({
    // Admin add == upsert a new object via PUT /admin/integration-objects (create-if-absent).
    mutationFn: ({
      key,
      externalObject,
    }: {
      key: string;
      externalObject: AddExternalObjectRequest;
    }) =>
      IntegrationObjectService.upsertObject(
        { accountId, origin: key },
        externalObject as Partial<ExternalObjectConf>,
      ),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
      queryClient.invalidateQueries({ queryKey: integrationObjectQueryKeys.all });
    },
  });
};

export const useDeleteExternalObject = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, originObjectKey }: { key: string; originObjectKey: string }) =>
      deleteExternalObject(key, originObjectKey, auth0),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

export const useUpdateExternalObject = () => {
  const queryClient = useQueryClient();
  // bagel-admin is cross-tenant: accountId comes from the /account/:accountId route.
  const { accountId } = useParams<{ accountId: string }>();

  return useMutation({
    // Route object edits through the admin PUT /admin/integration-objects (a `$set` merge,
    // so sending only the changed fields is safe) instead of the customer PATCH.
    mutationFn: ({
      key,
      originObjectKey,
      updates,
    }: {
      key: string;
      originObjectKey: string;
      updates: UpdateExternalObjectRequest;
    }) =>
      IntegrationObjectService.upsertObject({ accountId: accountId ?? '', origin: key }, {
        originObjectKey,
        ...updates,
      } as Partial<ExternalObjectConf>),
    onSuccess: (_, { key }) => {
      // Refresh both the (customer) detail view and the admin objects list.
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
      queryClient.invalidateQueries({ queryKey: integrationObjectQueryKeys.all });
    },
  });
};

/**
 * Hook to upload records directly to a specific integration object.
 * Uses the same processing logic as the sync API.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useUploadIntegrationRecords = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, recordsData }: { key: string; recordsData: UploadRecordsRequest }) =>
      uploadIntegrationRecords(key, recordsData, auth0),
    onSuccess: (_, { key }) => {
      // Invalidate integration queries to refresh the data
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
      // Also invalidate task queries since new records may create tasks
      queryClient.invalidateQueries({ queryKey: ['integrationTasks', key] });
    },
  });
};

/**
 * Hook to trigger object count exploration.
 */
export const useTriggerSyncCount = () => {
  const auth0 = useAuth0();
  return useMutation({
    mutationFn: ({
      key,
      originObjectKey,
      dateRange,
      dateField,
      filters,
    }: {
      key: string;
      originObjectKey: string;
      dateRange?: { start?: string; end?: string };
      dateField?: string;
      filters?: any;
    }) => triggerSyncCount(auth0, key, originObjectKey, dateRange, filters, dateField),
  });
};

/**
 * Hook to trigger a full initial sync for a specific integration object.
 * This will sync all items without date range restrictions.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useFullInitialSync = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      originObjectKey,
      ignoreExistingRanges,
    }: {
      key: string;
      originObjectKey: string;
      ignoreExistingRanges?: boolean;
    }) => fullInitialSync(key, originObjectKey, auth0, ignoreExistingRanges),
    onSuccess: (_, { key }) => {
      // Invalidate integration queries to refresh the data
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
      // Also invalidate task queries since new sync may create tasks
      queryClient.invalidateQueries({ queryKey: ['integrationTasks', key] });
    },
  });
};

/**
 * Hook to remap (reprocess) all original items for a specific integration object.
 * This marks all items as pending processing and triggers worker processing.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useRemap = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, originObjectKey }: { key: string; originObjectKey: string }) =>
      remap(key, originObjectKey, auth0),
    onSuccess: (_, { key, originObjectKey }) => {
      // Invalidate integration queries to refresh the data
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
      // Invalidate unprocessed item count query
      queryClient.invalidateQueries({
        queryKey: ['unprocessedItemCount', key, originObjectKey],
      });
      // Also invalidate task queries since remapping may create tasks
      queryClient.invalidateQueries({ queryKey: ['integrationTasks', key] });
    },
  });
};

/**
 * Hook to calculate field statistics for a specific integration object.
 * This triggers a worker message to calculate and save field statistics.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useCalculateFieldStats = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, originObjectKey }: { key: string; originObjectKey: string }) =>
      calculateFieldStats(key, originObjectKey, auth0),
    onSuccess: (_, { key, originObjectKey }) => {
      // Invalidate integration queries to refresh the data
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      // Invalidate explore fields query to refresh field statistics
      queryClient.invalidateQueries({
        queryKey: ['integrationExploreFields', key],
      });
    },
  });
};

/**
 * Hook to save selected OAuth scopes for an integration.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useSaveIntegrationSelectedScopes = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, selectedScopes }: { key: string; selectedScopes: string[] }) =>
      saveIntegrationSelectedScopes(key, selectedScopes, auth0),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

const setupStatusPollIntervalMs = (successfulPolls: number): number => {
  if (successfulPolls < 24) return 2500;
  if (successfulPolls < 48) return 10000;
  return 30000;
};

/**
 * Polls origin-object setup status until terminal state (ready / error),
 * backing off while it stays pending and stopping when it cannot be loaded.
 */
export const useOriginObjectSetupStatus = (
  key: string,
  originObjectKey: string | undefined,
  enabled: boolean,
) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: integrationKeys.originObjectSetupStatus(key, originObjectKey || ''),
    queryFn: () => {
      if (!originObjectKey) {
        return Promise.reject(new Error('originObjectKey is required'));
      }
      return getOriginObjectSetupStatus(key, originObjectKey, auth0);
    },
    enabled: !!auth0 && !!key && !!originObjectKey && enabled,
    staleTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => {
      const data = query.state.data as OriginObjectSetupStatus | undefined;
      if (data && (data.status === 'ready' || data.status === 'error')) return false;
      if (query.state.status === 'error') return data ? 30000 : false;
      if (!data) return 2500;
      return setupStatusPollIntervalMs(query.state.dataUpdateCount);
    },
  });
};

export const useSetupOriginObject = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      originObjectKey,
      options,
    }: {
      key: string;
      originObjectKey: string;
      options?: Record<string, unknown>;
    }) => setupOriginObject(key, originObjectKey, options, auth0),
    onSuccess: (_: SetupOriginObjectResponse, { key, originObjectKey }) => {
      queryClient.resetQueries({
        queryKey: integrationKeys.originObjectSetupStatus(key, originObjectKey),
      });
    },
  });
};

export const useIntegrationWebhooks = (key: string, enabled = true) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ['integrationWebhooks', key],
    queryFn: () => getIntegrationWebhooks(auth0, key),
    enabled: !!auth0 && !!key && enabled,
    staleTime: 10000,
  });
};

export const useCreateIntegrationWebhook = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      originObjectKey,
      event,
    }: {
      key: string;
      originObjectKey: string;
      event: string;
    }) => createIntegrationWebhook(auth0, key, { originObjectKey, event }),
    onSuccess: (_: IntegrationWebhookRecord, { key }) => {
      queryClient.invalidateQueries({ queryKey: ['integrationWebhooks', key] });
    },
  });
};

export const useUpdateIntegrationWebhook = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      webhookId,
      enabled,
    }: {
      key: string;
      webhookId: string;
      enabled: boolean;
    }) => updateIntegrationWebhook(auth0, key, webhookId, { enabled }),
    onSuccess: (_: IntegrationWebhookRecord, { key }) => {
      queryClient.invalidateQueries({ queryKey: ['integrationWebhooks', key] });
    },
  });
};

export const useDeleteIntegrationWebhook = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, webhookId }: { key: string; webhookId: string }) =>
      deleteIntegrationWebhook(auth0, key, webhookId),
    onSuccess: (_: void, { key }) => {
      queryClient.invalidateQueries({ queryKey: ['integrationWebhooks', key] });
      queryClient.invalidateQueries({ queryKey: ['webhookCallLogs', key] });
    },
  });
};

export const useIntegrationWebhookLogs = (
  key: string,
  webhookId: string | undefined,
  enabled = true,
) => {
  const auth0 = useAuth0();
  const limit = 20;
  return useInfiniteQuery({
    queryKey: ['webhookCallLogs', key, webhookId],
    queryFn: ({ pageParam }) =>
      getIntegrationWebhookLogs(auth0, key, webhookId!, pageParam as number, limit),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, total } = lastPage;
      if (page * lastPage.limit >= total) return undefined;
      return page + 1;
    },
    enabled: !!auth0 && !!key && !!webhookId && enabled,
  });
};
